<template>
  <section class="max-w-content mx-auto py-[50px] md:py-24">
    <div :class="['flex flex-col gap-16 items-center max-w-7xl mx-auto', addPaddingX ? 'max-sm:px-6 max-xl:px-13' : '']">
      <!-- Header -->
      <div class="flex flex-col gap-5 items-center max-w-3xl text-center w-full">
        <div class="flex flex-col gap-3 items-start w-full">
          <p v-if="tag" :class="['text-base font-semibold leading-6 text-brand-700 w-full', { 'text-grey-dark-300': useAlternativeTagTextColor }]">{{ tag }}</p>
          <h2
            class="text-3xl md:text-4xl font-semibold leading-[38px] md:leading-11 text-grey-dark-50 w-full flex items-center justify-center gap-3 flex-wrap"
          >
            <template v-if="titlePrefix || titleIcon || titleSuffix">
              <span v-if="titlePrefix">{{ titlePrefix }}</span>
              <img v-if="titleIcon" :src="titleIcon" alt="" class="inline-block h-8 md:h-10" />
              <span v-if="titleSuffix">{{ titleSuffix }}</span>
            </template>
            <template v-else-if="titleImage">
              <span v-for="(word, index) in title.split(' ')" :key="index">
                <img
                  v-if="word.toLowerCase() === 'layup'"
                  :src="getTitleImage()"
                  alt="LayUp Boost"
                  class="inline-block h-8 md:h-10"
                />
                <span v-else>{{ word }}</span>
              </span>
            </template>
            <span v-else v-html="title" />
          </h2>
        </div>
        <p class="text-base md:text-xl font-normal leading-6 md:leading-[30px] text-grey-dark-300 w-full">
          {{ description }}
        </p>
      </div>

      <!-- Desktop Grid / Mobile Carousel -->
      <div class="w-full">
        <!-- Desktop Grid (hidden on mobile) -->
        <div class="hidden md:flex md:justify-center gap-8 w-full flex-wrap">
          <slot name="card" v-for="(item, index) in items" :key="index" :item="item" :index="index"></slot>
        </div>

        <!-- Mobile Carousel (visible only on mobile) -->
        <div class="md:hidden">
          <Carousel
            class="w-full"
            :opts="{
              align: 'center',
              loop: false,
            }"
          >
            <CarouselContent class="-ml-6">
              <CarouselItem v-for="(item, index) in items" :key="index" class="pl-6 basis-full">
                <div class="carousel-slide-fill h-full">
                  <slot name="card" :item="item" :index="index"></slot>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselDotButtons class="mt-8" selected-class="border-brand-700 bg-grey-light-950" unselected-class="bg-white" />
          </Carousel>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts" generic="T">
import { Carousel, CarouselContent, CarouselDotButtons, CarouselItem } from '@/components/ui/carousel';
import { addBreadcrumb, createWarning } from '@/utils/monitoring.utils';

interface Props {
  tag?: string;
  title?: string;
  titleImage?: string;
  titlePrefix?: string;
  titleIcon?: string;
  titleSuffix?: string;
  description: string;
  items: T[];
  addPaddingX?: boolean;
  useAlternativeTagTextColor?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  addPaddingX: true,
  useAlternativeTagTextColor: false
});

const getTitleImage = () => {
  if (!props.titleImage) return '';
  try {
    return new URL(`../../assets/images/${props.titleImage}`, import.meta.url).href;
  } catch (e) {
    addBreadcrumb('image.load', `Failed to load image ${props.titleImage}`);
    createWarning(e);
    return '';
  }
};
</script>

<style scoped>
.carousel-slide-fill > :deep(*) {
  height: 100%;
}
</style>
